import { Component, Inject, OnInit } from '@angular/core';
import { AngularFireAuth } from '@newgenus/fire-compat/auth';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { OrgTemplateScope, PublishOrgAsTemplateRequest, PublishOrgAsTemplateResponse } from '@newgenus/common';
import { OrgCreationService } from '../services/org-creation.service';

/**
 * P4.c — Publish-as-template dialog.
 *
 * Triggered from the org-board's edit-mode toolbar by an owner of the
 * organization. The dialog reads the current user's owner status against
 * `organization.owners[]` (so non-owners see a disabled state with a helpful
 * message instead of a confusing 403 after submit), then calls
 * `api-orgCreationPublishOrgAsTemplate-default` via {@link OrgCreationService}.
 *
 * Returns the new `templateKey` on success so callers can offer "Open in
 * inbox" or copy-link UX in a follow-up.
 */
export interface PublishAsTemplateData {
    orgKey: string;
    /** Default value pre-filled into the name field; usually `organization.name`. */
    defaultName: string;
    /** `organization.owners[]` — drives the owner-only guard. */
    owners: string[];
}

export type PublishAsTemplateResult =
    | { kind: 'published'; templateKey: string; version: number; scope: OrgTemplateScope[] }
    | { kind: 'cancel' };

@Component({
    selector: 'newgenus-publish-as-template-dialog',
    templateUrl: './publish-as-template-dialog.component.html',
    styleUrls: ['./publish-as-template-dialog.component.scss'],
    standalone: false,
})
export class PublishAsTemplateDialogComponent implements OnInit {
    public name = '';
    public description = '';
    public visibility: 'org-private' | 'shared' = 'org-private';
    /** Comma- or newline-separated list of orgKeys for `visibility === 'shared'`. */
    public sharedWithRaw = '';

    // Template v2 — selectable scope.
    // `structure` is locked-on (the model is "you can't publish nothing").
    // Defaults match v1 behaviour so first-time publishers see no change.
    public includeFeatures = true;
    public includeSettings = true;
    public includeHats = false;
    public includeSGs = false;
    public includePolicies = false;

    public submitting = false;
    public ownerCheckPending = true;
    public isOwner = false;
    public ownerMessage = '';

    constructor(
        public dialogRef: MatDialogRef<PublishAsTemplateDialogComponent, PublishAsTemplateResult>,
        @Inject(MAT_DIALOG_DATA) public data: PublishAsTemplateData,
        private auth: AngularFireAuth,
        private orgCreation: OrgCreationService,
        private toastr: ToastrService
    ) {
        this.name = data.defaultName ?? '';
    }

    public async ngOnInit(): Promise<void> {
        try {
            const user = await firstValueFrom(
                this.auth.user.pipe(
                    filter((u) => !!u),
                    take(1)
                )
            );
            const uid = user?.uid ?? '';
            this.isOwner = !!uid && this.data.owners.includes(uid);
            if (!this.isOwner) {
                this.ownerMessage = 'Only an owner of this organization may publish it as a template.';
            }
        } finally {
            this.ownerCheckPending = false;
        }
    }

    public get canSubmit(): boolean {
        if (!this.isOwner || this.submitting) return false;
        if (!this.name.trim()) return false;
        if (this.visibility === 'shared' && this.parsedSharedWithKeys.length === 0) return false;
        return true;
    }

    public get parsedSharedWithKeys(): string[] {
        return this.sharedWithRaw
            .split(/[\s,]+/)
            .map((k) => k.trim())
            .filter((k) => k.length > 0);
    }

    /** Resolved scope sent to the server. Server further enforces 'structure'. */
    public get resolvedScope(): OrgTemplateScope[] {
        const scope: OrgTemplateScope[] = ['structure'];
        if (this.includeFeatures) scope.push('features');
        if (this.includeSettings) scope.push('settings');
        if (this.includeHats) scope.push('hats');
        if (this.includeSGs) scope.push('securityGroups');
        if (this.includePolicies) scope.push('policies');
        return scope;
    }

    /**
     * Dependency warnings (UX-level — server doesn't block, importer drops
     * dangling refs defensively). Each entry's `key` deduplicates the warning
     * so flipping checkboxes mid-form doesn't accumulate duplicates.
     */
    public get dependencyWarnings(): Array<{ key: string; message: string }> {
        const out: Array<{ key: string; message: string }> = [];
        if (this.includeHats && !this.includeSGs) {
            out.push({
                key: 'hats-need-sgs',
                message:
                    'Hats reference security groups. Without SGs the hat → SG links will be dropped on import — hats will land in the new org with no permissions attached.',
            });
        }
        if (this.includeHats && !this.includePolicies) {
            out.push({
                key: 'hats-need-policies',
                message:
                    "Hats reference policies via policyKeys. Without Policies, those per-hat policy bindings won't travel.",
            });
        }
        if (this.includeSGs && !this.includePolicies) {
            out.push({
                key: 'sgs-need-policies',
                message:
                    'Security groups reference policies. SG → policy links will be dropped on import without Policies.',
            });
        }
        return out;
    }

    public async submit(): Promise<void> {
        if (!this.canSubmit) return;
        this.submitting = true;
        const request: PublishOrgAsTemplateRequest = {
            orgKey: this.data.orgKey,
            name: this.name.trim(),
            description: this.description.trim(),
            visibility: this.visibility,
            scope: this.resolvedScope,
        };
        if (this.visibility === 'shared') {
            request.sharedWithOrgKeys = this.parsedSharedWithKeys;
        }
        try {
            const response: PublishOrgAsTemplateResponse = await firstValueFrom(
                this.orgCreation.publishOrgAsTemplate(request)
            );
            this.toastr.success(`Published "${request.name}" (v${response.version}).`, 'Publish as template');
            this.dialogRef.close({
                kind: 'published',
                templateKey: response.templateKey,
                version: response.version,
                scope: response.scope,
            });
        } catch (err: unknown) {
            const message = this.extractErrorMessage(err);
            console.error('[PublishAsTemplate] failed:', err);
            this.toastr.error(message, 'Publish as template');
            this.submitting = false;
        }
    }

    public cancel(): void {
        if (this.submitting) return;
        this.dialogRef.close({ kind: 'cancel' });
    }

    private extractErrorMessage(err: unknown): string {
        if (!err) return 'Publish failed.';
        const e = err as { error?: { error?: string; message?: string }; message?: string; status?: number };
        return (
            e.error?.error ??
            e.error?.message ??
            e.message ??
            (e.status ? `Publish failed (HTTP ${e.status}).` : 'Publish failed.')
        );
    }
}
